import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Chronometre } from './Chronometre'
import { ItemLine } from './ItemLine'
import { RelectureDesReponses } from './RelectureDesReponses'
import { VisageEnSilence } from './Visage'
import { BoutonArreter, ConfirmerLArret } from '@/features/parcours/ArreterLEpreuve'
import { Button } from '@/components/ui/Button'
import {
  enSoumission,
  envoyerReponses,
  lancerQuiz,
  nouvelIdentifiant,
  LONGUEURS,
  RYTHMES,
  type Carte,
  type Devoilement,
  type ReponseComposee,
} from '@/lib/api/feed'
import { sonFaux, sonJuste } from '@/lib/son'

/**
 * Le quiz chronométré — 02 §F2.4.
 *
 * Le seul des trois modes qui ressemble au jour du concours : un sujet précis,
 * un rythme imposé, un bilan.
 *
 * **Une question à la fois**, et pas de retour en arrière. Le fil se déroule ;
 * ici, on avance. C'est ce qui rend le chronomètre crédible : pouvoir revenir
 * en arrière permettrait de garder les questions difficiles pour la fin, ce
 * que le concours n'autorise pas.
 *
 * **Il ne débloque rien** (RG-2.30) : ni badge, ni certificat. Ses réponses
 * alimentent la maîtrise et la répétition espacée comme les autres — le
 * journal est unique (D-15), et cela ne demande aucun code particulier.
 */
export function Quiz(props: {
  tag: { path: string; label: string }
  economieDeDonnees?: boolean
  onQuitter: () => void
}) {
  return (
    <>
      {/* RG-D22ter — la mascotte se tait pendant une épreuve. Tout commentaire
          y est un indice : c'est RG-3.11quater étendue à ce qui n'est pas un
          verdict explicite mais en tient lieu. Monté ici plutôt que dans
          chacune des quatre branches de rendu du quiz — réglages, quiz vide,
          question, bilan — dont une seule aurait suffi à laisser parler le
          visage au mauvais moment. */}
      <VisageEnSilence />
      <DerouleDuQuiz {...props} />
    </>
  )
}

function DerouleDuQuiz({
  tag,
  economieDeDonnees = false,
  onQuitter,
}: {
  tag: { path: string; label: string }
  economieDeDonnees?: boolean
  onQuitter: () => void
}) {
  const client = useQueryClient()

  const [reglages, setReglages] = useState<{ questions: number; secondes: number } | null>(null)
  const [cartes, setCartes] = useState<Carte[]>([])
  const [rang, setRang] = useState(0)
  const [devoilements, setDevoilements] = useState<Record<string, Devoilement>>({})
  const [blancs, setBlancs] = useState<Record<string, true>>({})

  // Ce que le candidat a coché, question par question — RG-3.11quinquies. Le
  // dévoilement du serveur porte la BONNE réponse ; sans ceci, la relecture
  // afficherait le vert et rien d'autre, et le candidat ne saurait pas ce
  // qu'il avait choisi. Or c'est l'écart entre les deux qu'il vient lire.
  const [reponses, setReponses] = useState<Record<string, ReponseComposee>>({})

  // **Le verrou LOCAL, comme dans le Fil** (RG-2.3). Attendre le dévoilement
  // du serveur pour débloquer « Question suivante » laissait le candidat
  // devant une carte morte le temps d'un aller-retour — plusieurs secondes sur
  // une connexion haïtienne, pendant lesquelles il retape.
  const [envoyees, setEnvoyees] = useState<Record<string, true>>({})

  // La feuille d'arrêt — [DECISIONS §178].
  const [arretDemande, setArretDemande] = useState(false)

  const demarrage = useMutation({
    mutationFn: ({ questions, secondes }: { questions: number; secondes: number }) =>
      lancerQuiz(tag.path, questions, secondes),
    onSuccess: (page, variables) => {
      setCartes(page.data)
      setRang(0)
      setReglages(variables)
    },
  })

  const repondre = useMutation({
    mutationFn: ({
      carte,
      reponse,
      tempsMs,
    }: {
      carte: Carte
      reponse: ReponseComposee
      tempsMs: number
    }) =>
      envoyerReponses(
        [
          {
            client_uuid: nouvelIdentifiant(),
            form_version_id: carte.form_version_id,
            shown_option_ids: carte.options.map((o) => o.id),
            submitted: enSoumission(reponse),
            answered_at: new Date().toISOString(),
            time_spent_ms: tempsMs,
          },
        ],
        // RG-04-43 — le contexte compte : un quiz chronométré n'est PAS une
        // épreuve notée, et un échec n'y remet donc pas l'item à zéro.
        'timed_quiz',
      ),
    onSuccess: (resultat, { carte }) => {
      const [devoilement] = Object.values(resultat.reveals)

      if (devoilement !== undefined) {
        setDevoilements((avant) => ({ ...avant, [carte.item_id]: devoilement }))

        // §392 — le même son que dans le Fil : le quiz chronométré est du Fil
        // avec un chronomètre, et il serait étrange qu'il se taise.
        if (devoilement.is_correct) {
          sonJuste()
        } else {
          sonFaux()
        }
      }

      // Le quiz chronométré fait avancer l'objectif au même titre que le Fil
      // (RG-2.41) : sans invalidation, la pastille reste figée pendant les
      // vingt questions, puis saute d'un coup au retour au Fil.
      void client.invalidateQueries({ queryKey: ['progression'] })
    },
  })

  if (reglages === null) {
    return (
      <Reglages
        tag={tag}
        enCours={demarrage.isPending}
        onLancer={(questions, secondes) => {
          demarrage.mutate({ questions, secondes })
        }}
        onQuitter={onQuitter}
      />
    )
  }

  if (cartes.length === 0) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-[20px] font-bold">Pas encore de questions sur ce thème</p>
        <p className="mt-2 text-[15px]" style={{ color: 'var(--texte-2)' }}>
          Essayez un thème plus large, ou revenez quand des sujets auront été numérisés.
        </p>
        <div className="mt-4">
          <Button variant="outline" onClick={onQuitter}>
            Retour
          </Button>
        </div>
      </div>
    )
  }

  if (rang >= cartes.length) {
    return (
      <Bilan
        cartes={cartes}
        devoilements={devoilements}
        reponses={reponses}
        blancs={blancs}
        secondesParQuestion={reglages.secondes}
        economieDeDonnees={economieDeDonnees}
        onQuitter={onQuitter}
      />
    )
  }

  const carte = cartes[rang]

  if (carte === undefined) return null

  const repondue = envoyees[carte.item_id] === true || blancs[carte.item_id] === true

  return (
    <div>
      {/* **L'en-tête d'épreuve ne quitte jamais l'écran** — §108. Le rang, le
          thème et le chronomètre défilaient avec la page et disparaissaient
          sous la barre supérieure : à la troisième option, le candidat ne
          savait plus combien de temps il lui restait ni sur quoi il répondait.
          Ce sont les trois seules informations qu'une épreuve chronométrée doit
          garder sous l'œil. */}
      <header
        className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b px-4 py-3 backdrop-blur-md"
        style={{
          borderColor: 'var(--bordure)',
          background: 'color-mix(in srgb, var(--fond) 92%, transparent)',
        }}
      >
        <span className="flex min-w-0 flex-col">
          <span className="text-[13px]" style={{ color: 'var(--texte-2)' }}>
            Question {rang + 1} sur {cartes.length}
          </span>
          {/* Le thème monte ICI depuis la carte : répété sous l'en-tête, il
              défilait avec elle et n'était plus là quand il servait. */}
          <span className="truncate text-[15px] font-bold">{carte.tag.label}</span>
        </span>

        <span className="flex shrink-0 items-center gap-1">
          {repondue ? null : (
            <Chronometre
              // La clé remonte le composant à chaque question : l'état repart de
              // zéro tout seul, sans effet de réinitialisation.
              key={carte.item_id}
              secondes={reglages.secondes}
              onExpiration={() => {
                // RG-2.28 — « à l'expiration, la question passe et compte comme
                // non répondue ». Elle n'est PAS envoyée au journal : un blanc
                // que le candidat n'a pas donné n'apprend rien sur sa mémoire.
                setBlancs((avant) => ({ ...avant, [carte.item_id]: true }))
              }}
            />
          )}

          {/* §178 — la sortie de secours. Elle vit DANS l'en-tête collant, qui
              ne quitte jamais l'écran (§108) : un bouton d'arrêt qu'il faut
              faire défiler pour retrouver n'est pas une sortie. */}
          <BoutonArreter onArreter={() => { setArretDemande(true); }} />
        </span>
      </header>

      {/* **Ici les réponses sont DÉJÀ parties**, une par une — d'où
          `dejaComptees`. Écrire « tes réponses ne seront pas enregistrées »
          serait un mensonge que le compteur d'objectif démentirait dans la
          seconde. */}
      {arretDemande && (
        <ConfirmerLArret
          repondues={Object.keys(reponses).length}
          total={cartes.length}
          dejaComptees
          onAnnuler={() => { setArretDemande(false); }}
          onConfirmer={onQuitter}
        />
      )}

      <ItemLine
        key={carte.item_id}
        carte={carte}
        // RG-3.11quater — **aucun verdict pendant l'épreuve**, y compris ici :
        // un quiz par tag mesure comme les autres, et la correction immédiate
        // le transformerait en série d'indices. Ce qui a été répondu se relit
        // à la fin (RG-3.11quinquies), là où il sert à travailler.
        sansVerdict
        // **Le temps écoulé FERME la question** — RG-2.28, §106. Sans ce
        // verrou, les options restaient cliquables après l'expiration, la
        // réponse partait au journal et comptait dans le bilan : le
        // chronomètre ne mesurait plus rien.
        verrouillee={blancs[carte.item_id] === true}
        economieDeDonnees={economieDeDonnees}
        onRepondre={(reponse, tempsMs) => {
          setEnvoyees((avant) => ({ ...avant, [carte.item_id]: true }))
          setReponses((avant) => ({ ...avant, [carte.item_id]: reponse }))
          repondre.mutate({ carte, reponse, tempsMs })
        }}
      />

      {repondue ? (
        // `pb-24` : le bouton touchait le bouton central de la barre du bas,
        // qui déborde de 28 px au-dessus d'elle. Deux cibles tactiles qui se
        // chevauchent, c'est une tape sur deux qui part au mauvais endroit.
        <div className="px-4 pb-24 min-[688px]:pb-6">
          <Button
            onClick={() => {
              setRang((avant) => avant + 1)
            }}
          >
            {rang + 1 === cartes.length ? 'Voir le bilan' : 'Question suivante'}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function Reglages({
  tag,
  enCours,
  onLancer,
  onQuitter,
}: {
  tag: { path: string; label: string }
  enCours: boolean
  onLancer: (questions: number, secondes: number) => void
  onQuitter: () => void
}) {
  // **Dix questions, vingt secondes** — RG-2.26 précisée, §107. Vingt
  // questions à trente secondes font dix minutes : c'est une séance, pas un
  // essai, et on ne la lance pas entre deux cours. Dix à vingt secondes en font
  // trois — le geste qu'on refait plusieurs fois par jour.
  const [questions, setQuestions] = useState<number>(10)
  const [secondes, setSecondes] = useState<number>(20)

  return (
    <div className="flex flex-col gap-6 px-4 py-6">
      <header>
        <h2 className="text-[20px] font-bold">Quiz — {tag.label}</h2>
        <p className="mt-1 text-[15px]" style={{ color: 'var(--texte-2)' }}>
          {/* RG-2.30 — le dire d'emblée : c'est un entraînement, pas une note. */}
          Un entraînement au rythme du concours. Il ne compte dans aucune note.
        </p>
      </header>

      <Choix
        legende="Combien de questions ?"
        valeurs={[...LONGUEURS]}
        actif={questions}
        suffixe=""
        onChoisir={setQuestions}
      />

      <Choix
        legende="Combien de temps par question ?"
        valeurs={[...RYTHMES]}
        actif={secondes}
        suffixe=" s"
        onChoisir={setSecondes}
      />

      <p className="text-[13px]" style={{ color: 'var(--texte-2)' }}>
        Durée totale : environ {Math.round((questions * secondes) / 60)} minutes.
      </p>

      <div className="flex gap-3">
        <Button
          loading={enCours}
          onClick={() => {
            onLancer(questions, secondes)
          }}
        >
          Commencer
        </Button>
        <Button variant="ghost" onClick={onQuitter}>
          Annuler
        </Button>
      </div>
    </div>
  )
}

function Choix({
  legende,
  valeurs,
  actif,
  suffixe,
  onChoisir,
}: {
  legende: string
  valeurs: number[]
  actif: number
  suffixe: string
  onChoisir: (valeur: number) => void
}) {
  return (
    <fieldset>
      <legend className="pb-2 text-[15px] font-bold">{legende}</legend>

      <div className="flex flex-wrap gap-2">
        {valeurs.map((valeur) => {
          const choisi = valeur === actif

          return (
            <button
              key={valeur}
              type="button"
              aria-pressed={choisi}
              onClick={() => {
                onChoisir(valeur)
              }}
              style={
                choisi
                  ? { borderColor: 'var(--accent)', color: 'var(--accent-fort)' }
                  : { borderColor: 'var(--bordure)' }
              }
              className="min-h-11 rounded-full border px-4 text-[15px] font-bold"
            >
              {valeur}
              {suffixe}
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}

/**
 * Le bilan — RG-2.29.
 *
 * Score, temps moyen, et surtout la **répartition des erreurs par sous-tag** :
 * un score seul dit au candidat qu'il a échoué, pas ce qu'il doit reprendre.
 */
function Bilan({
  cartes,
  devoilements,
  reponses,
  blancs,
  secondesParQuestion,
  economieDeDonnees = false,
  onQuitter,
}: {
  cartes: Carte[]
  devoilements: Record<string, Devoilement>
  reponses: Record<string, ReponseComposee>
  blancs: Record<string, true>
  secondesParQuestion: number
  economieDeDonnees?: boolean
  onQuitter: () => void
}) {
  const justes = cartes.filter((c) => devoilements[c.item_id]?.is_correct === true).length
  const nonRepondues = cartes.filter((c) => blancs[c.item_id] === true).length

  const erreursParTheme = new Map<string, number>()

  for (const carte of cartes) {
    const devoilement = devoilements[carte.item_id]
    const ratee = blancs[carte.item_id] === true || devoilement?.is_correct === false

    if (ratee) {
      erreursParTheme.set(carte.tag.label, (erreursParTheme.get(carte.tag.label) ?? 0) + 1)
    }
  }

  const classees = [...erreursParTheme.entries()].sort((a, b) => b[1] - a[1])

  return (
    <div className="flex flex-col gap-6 px-4 py-6">
      <header>
        <h2 className="text-[31px] font-extrabold tabular-nums">
          {justes} / {cartes.length}
        </h2>
        <p className="mt-1 text-[15px]" style={{ color: 'var(--texte-2)' }}>
          {nonRepondues > 0
            ? `${String(nonRepondues)} question${nonRepondues > 1 ? 's' : ''} non répondue${nonRepondues > 1 ? 's' : ''} — le temps est une compétence.`
            : 'Toutes les questions ont été répondues dans le temps.'}
        </p>
        <p className="mt-1 text-[13px]" style={{ color: 'var(--texte-2)' }}>
          {secondesParQuestion} s par question.
        </p>
      </header>

      {classees.length > 0 ? (
        <section>
          <h3 className="pb-2 text-[15px] font-bold">Ce qu’il faut reprendre</h3>

          <ul className="flex flex-col gap-1">
            {classees.map(([theme, combien]) => (
              <li key={theme} className="flex justify-between text-[15px]">
                <span>{theme}</span>
                <span style={{ color: 'var(--texte-2)' }}>
                  {combien} erreur{combien > 1 ? 's' : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <p className="text-[15px]" style={{ color: 'var(--texte-2)' }}>
          Aucune erreur. Essayez un rythme plus serré ou un thème plus large.
        </p>
      )}

      {/* RG-3.11quinquies — le score dit qu'il y a un problème ; la relecture
          est le seul endroit où il se répare. */}
      <RelectureDesReponses
        cartes={cartes}
        devoilements={devoilements}
        reponses={reponses}
        economieDeDonnees={economieDeDonnees}
      />

      <div>
        <Button onClick={onQuitter}>Retour au fil</Button>
      </div>
    </div>
  )
}
